import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { label, projectMonth } from '@entities/ksp/format';
import type { Activity, KspBusinessError, KspWorkspace } from '@entities/ksp/types';
import type { FormField, FormSpec } from '@features/ksp-actions/ActionForm';
import type { useMutateKspMutation } from '@shared/api/endpoints/ksp';
import {
  OBJECT_EXPORTS,
  choice,
  dateField,
  optional,
  textField,
} from '../types';

export interface UseWorkspaceActionsParams {
  projectId: string;
  versionId?: string | null;
  data?: KspWorkspace;
  params: URLSearchParams;
  mutate: ReturnType<typeof useMutateKspMutation>[0];
  setMessage: (msg: string) => void;
  setErrors: Dispatch<SetStateAction<string[]>>;
  setExportId: (id: string) => void;
  wbsOptions: { value: string; label: string }[];
  calendarOptions: { value: string; label: string }[];
  fileOptions: { value: string; label: string }[];
  workOptions: { value: string; label: string }[];
  exportObjects: { value: string; label: string; kind: string; snapshot_id?: string }[];
}

export function useWorkspaceActions({
  projectId,
  versionId,
  data,
  params,
  mutate,
  setMessage,
  setErrors,
  setExportId,
  wbsOptions,
  calendarOptions,
  fileOptions,
  workOptions,
  exportObjects,
}: UseWorkspaceActionsParams) {
  const [form, setForm] = useState<FormSpec | null>(null);
  const formSeq = useRef(0);

  const fail = (error: unknown) => {
    if (error instanceof Error) {
      setErrors((previous) => [...previous, error.message]);
      return;
    }
    const response = error as {
      status?: number;
      data?: { error?: KspBusinessError | string; detail?: KspBusinessError } & KspBusinessError;
    };
    const raw = response.data?.error ?? response.data?.detail ?? response.data;
    const detail: KspBusinessError | undefined =
      typeof raw === 'string' ? { message: raw, code: response.data?.code } : raw;
    const reason =
      response.status === 409
        ? 'Версия уже изменилась. Обновите данные и повторно проверьте действие.'
        : (detail?.message ??
          'Операция не выполнена. Проверьте права, исходные данные и соединение.');
    setErrors((prev) => [
      ...prev,
      `${detail?.code ? `${detail.code}: ` : ''}${reason}${detail?.field ? ` · поле ${detail.field}` : ''}`,
    ]);
  };

  const execute = async (
    path: string,
    body: Record<string, unknown>,
    method: 'POST' | 'PATCH' | 'DELETE' = 'POST',
  ) => {
    if (!data) return;
    try {
      if (data.history_read_only)
        throw new Error(
          'Исторический срез доступен только для чтения. Откройте текущие данные проекта для изменений.',
        );
      const result = await mutate({
        projectId,
        path,
        method,
        body: { expected_version: data.version, ...body },
        key: crypto.randomUUID(),
      }).unwrap();
      setMessage('Действие выполнено. Данные перечитаны с сервера.');
      setForm(null);
      return result;
    } catch (error) {
      fail(error);
      throw error;
    }
  };

  const open = (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => {
    if (data?.history_read_only) return;
    formSeq.current += 1;
    setForm({
      id: formSeq.current,
      title,
      fields,
      submit,
      onSubmit: async (v) => {
        try {
          await action(v);
        } catch (error) {
          if (error instanceof Error) fail(error);
        }
      },
    });
  };

  const registerDispatchEvidence = (letterId: string) =>
    open(
      'Зарегистрировать доказательство направления',
      [
        choice('channel', 'Канал направления', [
          { value: 'smtp', label: 'Почта' },
          { value: 'edi', label: 'ЭДО' },
          { value: 'manual', label: 'Документальная отправка' },
        ]),
        { ...textField('dispatched_at', 'Время направления UTC'), type: 'datetime-local' },
        textField('reference', 'Идентификатор отправки'),
        textField('message_id', 'Message-ID почтового отправления', false),
        textField('recipients', 'Адресаты Заказчика, через запятую'),
        choice('evidence_file_id', 'Подтверждающий файл', fileOptions),
        textField('verification_note', 'Результат сверки'),
      ],
      (values) =>
        execute(`letters/${letterId}/dispatch-evidence`, {
          ...values,
          dispatched_at: new Date(`${values.dispatched_at}:00Z`).toISOString(),
          recipients: values.recipients.split(',').map((value) => value.trim()),
        }),
    );

  const activityForm = (activity?: Activity) =>
    open(
      activity ? `Работа ${activity.code}` : 'Новая работа',
      [
        textField('code', 'Код работы', true, activity?.code),
        textField('name', 'Наименование', true, activity?.name),
        choice(
          'kind',
          'Тип работы',
          [
            { value: 'work', label: 'Работа' },
            { value: 'milestone', label: 'Расчётная веха' },
          ],
          activity?.kind ?? 'work',
        ),
        { ...choice('wbs_id', 'Раздел WBS', wbsOptions, activity?.wbs_id ?? ''), required: false },
        choice('calendar_id', 'Календарь', calendarOptions, activity?.calendar_id),
        textField('quantity', 'Подтверждённый объём', false, activity?.quantity ?? ''),
        textField('unit', 'Единица измерения', false, activity?.unit ?? ''),
        {
          ...textField(
            'duration_minutes',
            'Длительность, рабочие минуты',
            true,
            activity?.duration_minutes?.toString(),
          ),
          type: 'number',
        },
        {
          ...textField(
            'remaining_duration_minutes',
            'Остаточная длительность, минуты',
            false,
            activity?.remaining_duration_minutes?.toString(),
          ),
          type: 'number',
        },
        textField('labour_weight', 'Утверждённый вес', false, activity?.labour_weight ?? ''),
        {
          ...choice(
            'resource_id',
            'Назначить ресурс',
            data?.resources.map((resource) => ({ value: resource.id, label: resource.name })) ?? [],
          ),
          required: false,
        },
        textField('resource_demand', 'Потребность ресурса на рабочую минуту', false),
        textField(
          'earliest_start',
          'Начало не ранее, UTC',
          false,
          activity?.earliest_start ??
            (typeof activity?.constraints?.earliest_start === 'string'
              ? activity.constraints.earliest_start
              : ''),
        ),
        textField(
          'latest_finish',
          'Окончание не позднее, UTC',
          false,
          activity?.latest_finish ??
            (typeof activity?.constraints?.latest_finish === 'string'
              ? activity.constraints.latest_finish
              : ''),
        ),
        choice(
          'actual_source_policy',
          'Источник принятого факта',
          [
            { value: 'field', label: 'Прораб' },
            { value: 'pto', label: 'ПТО' },
            { value: 'explicit_split', label: 'Явное разделение' },
          ],
          activity?.actual_source_policy ?? 'field',
        ),
      ],
      async (values) =>
        execute(
          `versions/${versionId}/activities${activity ? `/${activity.id}` : ''}`,
          {
            ...values,
            earliest_start: values.earliest_start || null,
            latest_finish: values.latest_finish || null,
            resource_demands: values.resource_id
              ? [
                  ...(activity?.resource_demands.filter(
                    (demand) => demand.resource_id !== values.resource_id,
                  ) ?? []),
                  { resource_id: values.resource_id, quantity: values.resource_demand },
                ]
              : (activity?.resource_demands ?? []),
            source_refs: activity?.source_refs ?? [],
            provenance: { source_refs: activity?.source_refs ?? [] },
            constraints: {
              ...activity?.constraints,
              earliest_start: values.earliest_start || null,
              latest_finish: values.latest_finish || null,
            },
            wbs_id: values.wbs_id || null,
            version_id: versionId,
            ...optional('id', activity?.id ?? ''),
            quantity: values.quantity || null,
            unit: values.unit || null,
            labour_weight: values.labour_weight || null,
            duration_minutes: values.duration_minutes ? Number(values.duration_minutes) : null,
            remaining_duration_minutes: values.remaining_duration_minutes
              ? Number(values.remaining_duration_minutes)
              : null,
          },
          activity ? 'PATCH' : 'POST',
        ),
    );

  const createDocument = (kind: string) => {
    if (kind === 'recovery_plan') {
      open(
        'План ликвидации отставания',
        [
          choice(
            'deviation_act_id',
            'Акт фиксации отклонений',
            data?.documents
              .filter((doc) => doc.kind === 'deviation_act')
              .map((doc) => ({ value: doc.id, label: doc.title })) ?? [],
          ),
          choice(
            'baseline_id',
            'Утверждённый базовый график',
            data?.versions
              .filter(
                (v) =>
                  ['contract', 'detailed_baseline'].includes(v.kind) &&
                  ['approved_internal', 'approved_customer'].includes(v.approval),
              )
              .map((v) => ({ value: v.id, label: v.name ?? `Базовый график ${v.revision}` })) ?? [],
          ),
          {
            ...choice(
              'scenario_id',
              'Сценарий графика',
              data?.versions
                .filter((v) => v.kind === 'scenario')
                .map((v) => ({ value: v.id, label: v.name ?? `Сценарий ${v.revision}` })) ?? [],
            ),
            required: false,
          },
          textField('basis', 'Основание и согласование'),
          choice('activity_id', 'Работа мероприятия', workOptions),
          textField('title', 'Первое мероприятие'),
          textField('responsible_id', 'Ответственный'),
          dateField('due_date', 'Срок мероприятия'),
          textField('resources', 'Требуемые ресурсы'),
          textField('expected_effect', 'Ожидаемый эффект'),
          { ...choice('source_file_id', 'Обоснование', fileOptions), required: false },
        ],
        (values) =>
          execute(`projects/${projectId}/recovery-plans`, {
            deviation_act_id: values.deviation_act_id,
            baseline_id: values.baseline_id,
            ...optional('scenario_id', values.scenario_id),
            basis: values.basis,
            actions: [
              {
                activity_id: values.activity_id,
                title: values.title,
                responsible_id: values.responsible_id,
                due_date: values.due_date,
                resources: [
                  { description: values.resources, expected_effect: values.expected_effect },
                ],
                ...optional('source_file_id', values.source_file_id),
              },
            ],
          }),
      );
    } else {
      open(
        'Акт фиксации отклонений',
        [
          dateField('period_start', 'Начало периода'),
          dateField('period_end_exclusive', 'Конец периода (не включается)'),
          { ...textField('reasons', 'Причины и пояснения сторон'), type: 'textarea' },
          choice('source_file_id', 'Подтверждающее приложение', fileOptions),
        ],
        (values) =>
          execute(`projects/${projectId}/deviation-acts`, {
            ...values,
            snapshot_id: data?.summary?.snapshot_id,
            reasons: [values.reasons],
          }),
      );
    }
  };

  const exportForm = () =>
    open(
      'Выгрузить зафиксированный документ',
      [
        choice(
          'kind',
          'Документ',
          ['schedule', 'period_plan', 'planfact', 'deviation_act', 'recovery_plan', 'letter'].map(
            (value) => ({ value, label: label(value) }),
          ),
          'schedule',
        ),
        choice(
          'format',
          'Формат',
          ['xlsx', 'csv', 'json', 'docx', 'pdf'].map((value) => ({
            value,
            label: value.toUpperCase(),
          })),
          'xlsx',
        ),
        {
          ...choice(
            'export_profile',
            'Назначение',
            [
              { value: 'working', label: 'Рабочий документ / для передачи' },
              { value: 'full', label: 'Полный пакет обмена и проверки' },
            ],
            'working',
          ),
          help: 'Рабочий документ — график или МСГ в XLSX, DOCX, PDF без служебных идентификаторов и листов обмена. CSV, JSON и прочие документы всегда выгружаются полным пакетом.',
        },
        choice(
          'page_size',
          'Размер листа',
          [
            { value: 'A4', label: 'A4, альбомный' },
            { value: 'A3', label: 'A3, альбомный' },
          ],
          'A3',
        ),
        textField(
          'period',
          'Период',
          false,
          params.get('period') ??
            data?.selected_period ??
            data?.summary?.selected_period ??
            (data?.summary ? projectMonth(data.summary.data_date, data.summary.timezone) : ''),
        ),
        {
          key: 'object_id',
          label: 'Объект документа (задание периода, акт, план, письмо)',
          type: 'select',
          required: false,
          value:
            [...(data?.period_plans ?? [])].sort((a, b) =>
              a.status === 'released' && b.status !== 'released' ? -1 : 0,
            )[0]?.id ?? '',
          options: exportObjects.map(({ value, label: text }) => ({ value, label: text })),
        },
      ],
      async (values) => {
        const object = OBJECT_EXPORTS.includes(values.kind)
          ? exportObjects.find(
              (item) => item.value === values.object_id && item.kind === values.kind,
            )
          : undefined;
        const printable =
          ['schedule', 'period_plan'].includes(values.kind) &&
          ['xlsx', 'docx', 'pdf'].includes(values.format);
        const result = await execute('exports', {
          ...values,
          export_profile: printable && values.export_profile === 'working' ? 'working' : 'full',
          object_id: object?.value,
          project_id: projectId,
          snapshot_id:
            object?.snapshot_id ??
            params.get('snapshot') ??
            data?.selected_snapshot_id ??
            data?.summary?.snapshot_id,
          schedule_version_id: versionId,
        });
        if (typeof (result as { id?: string })?.id === 'string')
          setExportId((result as { id: string }).id);
      },
      'Создать выгрузку',
    );

  return {
    form,
    setForm,
    fail,
    execute,
    open,
    registerDispatchEvidence,
    activityForm,
    createDocument,
    exportForm,
  };
}
